/**
 * A player's head, as 8×8 pixels — drawn from their real skin when the server sends its texture hash,
 * and otherwise GENERATED from their uuid so the page asks nobody anything.
 *
 * WHY TWO WAYS. A real head means the browser fetches the skin from Mojang's texture host, which is a
 * request carrying the player's address to a third party; whether that is acceptable is the server
 * operator's decision (the mod sends `skin` only when it is configured to — see `AudioCodeRequest`),
 * the same stance `me/identity.ts` takes for `/me`. Without it the page still shows a head: a
 * deterministic face in a Minecraft-ish palette — hair, skin, eyes, mouth — that is the same on every
 * device and identifies nobody (it is a hash, not a likeness).
 */
import { hashText } from '../me/identity';

/** `textures.minecraft.net` for a skin hash. The page crops the 64×64 skin's face and hat by CSS. */
export function skinUrl(hash: string): string {
  return `https://textures.minecraft.net/texture/${hash}`;
}

const SKIN_TONES = ['#f2c9a1', '#e0ac7e', '#c68a5c', '#a86b45', '#7d4b2e', '#f6d7c3'] as const;
const HAIR = ['#3b2414', '#6b4423', '#c9a15b', '#1f1f24', '#8f3b1d', '#d9d4c7', '#6b4fa0', '#2c2760'] as const;
const EYES = ['#3f76c8', '#3b8f5a', '#5c3b1e', '#6b4fa0', '#2a9db0'] as const;

/** 64 colours, row by row, for the generated face. Pure: the same uuid always draws the same face. */
export function generatedFace(uuid: string): string[] {
  const hash = hashText(uuid);
  const skin = SKIN_TONES[hash % SKIN_TONES.length] as string;
  const hair = HAIR[(hash >>> 3) % HAIR.length] as string;
  const eye = EYES[(hash >>> 6) % EYES.length] as string;
  const fringe = (hash >>> 9) % 3; // how far the hair comes down the forehead
  const beard = ((hash >>> 11) & 3) === 0;
  const shade = shadeOf(skin);
  const rows: string[][] = [];
  for (let y = 0; y < 8; y += 1) {
    const row: string[] = [];
    for (let x = 0; x < 8; x += 1) {
      let colour = skin;
      if (y < 2) colour = hair;
      else if (y === 2) colour = x === 0 || x === 7 || fringe >= 1 ? hair : skin;
      else if (y === 3 && (x === 0 || x === 7) && fringe === 2) colour = hair;
      if (y === 4 && (x === 1 || x === 5)) colour = '#ffffff';
      if (y === 4 && (x === 2 || x === 6)) colour = eye;
      if (y === 5 && (x === 3 || x === 4)) colour = shade;
      if (y === 6 && x >= 2 && x <= 5) colour = beard ? hair : '#7a3a2c';
      if (y === 7 && beard && x >= 1 && x <= 6) colour = hair;
      row.push(colour);
    }
    rows.push(row);
  }
  return rows.flat();
}

function shadeOf(hex: string): string {
  const value = Number.parseInt(hex.slice(1), 16);
  const channel = (shift: number): number => Math.round(((value >> shift) & 0xff) * 0.82);
  return `#${[16, 8, 0].map((shift) => channel(shift).toString(16).padStart(2, '0')).join('')}`;
}
